import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, Sparkles, CheckCircle2, AlertTriangle, TrendingUp, ArrowRight,
  Clapperboard, Swords, Quote,
} from "lucide-react";
import useApi from "../../hooks/useApi";
import Speedometer from "../../components/Speedometer";
import { scoreColor, COMPONENT_ICON } from "../../lib/sales";

export default function HealthCheckReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useApi(`/sales/health-checks/${id}`, [id]);

  if (loading) return <div className="p-10 text-ink-3">Loading…</div>;
  if (!data || !data.report) {
    return (
      <div className="p-8 max-w-xl">
        <Link to="/sales" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink mb-6"><ChevronLeft size={16} /> Sales Director</Link>
        <div className="card p-10 text-center">
          <p className="font-semibold text-ink">No report generated yet.</p>
          <button onClick={() => navigate(`/sales/check/${id}`)} className="btn-primary focus-ring mt-5 mx-auto">Go to analysis <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  const { prospect, overall_score, band, potential, opportunities, nichecom, components, report, competitors } = data;
  const keyMetrics = (data.metrics || []).filter((m) => ["per_week", "video_pct", "property_pct", "engagement_rate", "google_rating"].includes(m.key));

  return (
    <div style={{ background: "#F4F5F1" }} className="min-h-screen pb-20">
      {/* internal bar */}
      <div className="px-6 sm:px-8 py-3 flex items-center gap-3 border-b bg-white" style={{ borderColor: "#E7E8E4" }}>
        <Link to={`/sales/check/${id}`} className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"><ChevronLeft size={16} /> Back to analysis</Link>
        <span className="ml-auto text-xs text-ink-3">Customer-facing report</span>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-10">
        {/* COVER */}
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "var(--grad-ink)" }} data-testid="report-cover">
          <div className="blob" style={{ width: 520, height: 520, top: -220, right: -140, background: "radial-gradient(circle, rgba(147,192,32,.3), transparent 70%)" }} />
          <div className="relative z-10 p-8 sm:p-12">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}><Sparkles size={14} /> Growth · Social Health Check</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mt-4">{prospect.company}</h1>
            <p className="text-lg mt-1" style={{ color: "rgba(255,255,255,.6)" }}>{prospect.branch}{prospect.postcode ? ` · ${prospect.postcode}` : ""}</p>
            <div className="flex flex-col md:flex-row items-center gap-8 mt-8">
              <Speedometer value={overall_score} size={260} dark label="Social Health Score" duration={1600} />
              <div className="flex-1">
                <div className="text-2xl font-extrabold text-white">{band}</div>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,.8)" }}>{report.executive_summary}</p>
              </div>
            </div>
          </div>
        </div>

        {/* KEY METRICS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
          {keyMetrics.map((m) => (
            <div key={m.key} className="rounded-2xl bg-white border p-4" style={{ borderColor: "#E7E8E4" }}>
              <div className="text-2xl font-extrabold text-ink tabular-nums">{m.value}{m.unit === "%" ? "%" : ""}</div>
              <div className="text-xs text-ink-2 mt-1 leading-tight">{m.name}</div>
            </div>
          ))}
        </div>

        {/* STRENGTHS / WEAKNESSES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <div className="rounded-2xl bg-white border p-6" style={{ borderColor: "#E7E8E4" }}>
            <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={18} style={{ color: "#5F8213" }} /><span className="font-bold text-ink">Strengths</span></div>
            <div className="space-y-2">{report.strengths.map((s) => <div key={s} className="flex items-center gap-2 text-sm text-ink"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#93C020" }} /> {s}</div>)}
              {report.strengths.length === 0 && <p className="text-sm text-ink-3">Building from the ground up.</p>}</div>
          </div>
          <div className="rounded-2xl bg-white border p-6" style={{ borderColor: "#E7E8E4" }}>
            <div className="flex items-center gap-2 mb-3"><AlertTriangle size={18} style={{ color: "#C0473F" }} /><span className="font-bold text-ink">Where you're losing ground</span></div>
            <div className="space-y-2">{report.weaknesses.map((s) => <div key={s} className="flex items-center gap-2 text-sm text-ink"><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C0473F" }} /> {s}</div>)}</div>
          </div>
        </div>

        {/* SCORE BREAKDOWN */}
        <h2 className="text-xl font-extrabold text-ink mt-10 mb-4">Where your score comes from</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {components.map((c) => {
            const cc = scoreColor(c.score); const Icon = COMPONENT_ICON[c.key];
            return (
              <div key={c.key} className="rounded-2xl bg-white border p-4 flex items-center gap-4" style={{ borderColor: "#E7E8E4" }}>
                <span className="grid place-items-center w-10 h-10 rounded-lg" style={{ background: cc.bg }}>{Icon && <Icon size={18} strokeWidth={1.75} style={{ color: cc.fg }} />}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink">{c.name}</div>
                  <div className="mt-1.5 h-1.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}><div className="h-full rounded-full" style={{ width: `${c.score}%`, background: cc.fg }} /></div>
                </div>
                <div className="text-xl font-extrabold tabular-nums" style={{ color: cc.fg }}>{c.score}</div>
              </div>
            );
          })}
        </div>

        {/* NICHECOM */}
        {nichecom && (
          <div className="rounded-2xl p-6 sm:p-8 mt-8" style={{ background: "var(--grad-green-soft)", border: "1px solid rgba(147,192,32,.35)" }}>
            <div className="flex items-center gap-2 mb-4"><Clapperboard size={18} style={{ color: "#5F8213" }} /><span className="font-bold text-ink">You already have {nichecom.missed} pieces of unused content</span></div>
            <div className="grid grid-cols-3 gap-4">
              {[["Produced", nichecom.produced], ["Used", nichecom.used], ["Unused", nichecom.missed]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-white p-5 text-center"><div className="text-3xl font-extrabold tabular-nums text-ink">{v}</div><div className="label-caps mt-1">{l}</div></div>
              ))}
            </div>
          </div>
        )}

        {/* COMPETITORS */}
        {competitors?.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3"><Swords size={18} className="text-ink-2" /><h2 className="text-xl font-extrabold text-ink">You vs local competitors</h2></div>
            <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "#E7E8E4" }}>
              <div className="grid gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider text-ink-3 border-b" style={{ borderColor: "#E7E8E4", gridTemplateColumns: `1.2fr repeat(${competitors.length + 1}, 1fr)` }}>
                <span>Metric</span><span className="text-center">You</span>{competitors.map((c) => <span key={c.id} className="text-center truncate">{c.agency}</span>)}
              </div>
              {[["Overall score", overall_score, (c) => c.score], ["Posts / week", data.metrics.find((m) => m.key === "per_week")?.value, (c) => c.metrics.per_week.value]].map(([label, you, get]) => (
                <div key={label} className="grid gap-2 px-4 py-3 text-sm border-b last:border-0" style={{ borderColor: "#F0F1EC", gridTemplateColumns: `1.2fr repeat(${competitors.length + 1}, 1fr)` }}>
                  <span className="text-ink-2">{label}</span><span className="text-center font-bold tabular-nums text-ink">{you}</span>{competitors.map((c) => <span key={c.id} className="text-center tabular-nums text-ink-2">{get(c)}</span>)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CURRENT VS POTENTIAL */}
        {potential && (
          <div className="rounded-3xl p-8 sm:p-12 mt-10 text-center relative overflow-hidden" style={{ background: "var(--grad-ink)" }}>
            <div className="blob" style={{ width: 460, height: 460, bottom: -220, left: -120, background: "radial-gradient(circle, rgba(147,192,32,.28), transparent 70%)" }} />
            <div className="relative z-10">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}>Your growth opportunity</div>
              <h2 className="text-3xl font-extrabold text-white mt-2">A realistic path from {potential.current} to {potential.overall}</h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-8">
                <Speedometer value={potential.current} size={210} dark label="Today" duration={1400} />
                <div><div className="text-5xl font-extrabold" style={{ color: "#B6E14F" }}>+{potential.delta}</div><div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,.55)" }}>Points</div></div>
                <Speedometer value={potential.overall} size={210} dark label="With Growth" duration={1400} />
              </div>
            </div>
          </div>
        )}

        {/* TOP ACTIONS */}
        {opportunities?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-extrabold text-ink mb-4">Top recommended actions</h2>
            <div className="space-y-3">
              {opportunities.map((o) => (
                <div key={o.rank} className="rounded-2xl bg-white border p-5 flex gap-4" style={{ borderColor: "#E7E8E4" }}>
                  <span className="text-2xl font-extrabold tabular-nums" style={{ color: "#93C020" }}>{String(o.rank).padStart(2, "0")}</span>
                  <div><div className="font-bold text-ink uppercase tracking-tight">{o.title}</div><p className="text-sm text-ink-2 mt-1 leading-relaxed">{o.body}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-3xl p-10 mt-10 text-center" style={{ background: "var(--grad-green)" }}>
          <Quote size={28} className="mx-auto" color="#16180F" />
          <h2 className="text-3xl font-extrabold tracking-tight mt-3" style={{ color: "#16180F" }}>Your growth opportunity is real — and reachable.</h2>
          <p className="text-sm mt-2 max-w-lg mx-auto" style={{ color: "rgba(22,24,15,.7)" }}>Growth measures it, prioritises it, and — with Managed — executes it for you.</p>
          <button onClick={() => navigate("/app")} data-testid="report-cta" className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold" style={{ background: "#16180F", color: "#fff" }}>
            See how Growth gets you there <ArrowRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
